// Team Comparison — every engineer across the quality pillars, sortable, with a
// relative-strength heatmap and auto coaching signals. Ported from qa-live
// (QA-2286), dark-theme inline styles.

import { useMemo, useState } from 'react'
import { getInitials } from './pulseHelpers.js'
import { colorFromName } from './pulseHelpers.js'
import { AVATAR_PALETTE, PULSE_COLORS } from './pulseConfig.js'

const avatarColor = (name) => colorFromName(name, AVATAR_PALETTE)

const COLUMNS = [
    { key: 'score', label: 'Impact', emoji: '⭐', rgb: '99,102,241' },
    { key: 'bugsCreated', label: 'Found', emoji: '🐛', rgb: '239,68,68' },
    { key: 'bugsClosed', label: 'Closed', emoji: '✅', rgb: '16,185,129' },
    { key: 'qaStoriesClosed', label: 'QA Stories', emoji: '📋', rgb: '139,92,246' },
    { key: 'storiesAutomated', label: 'Automated', emoji: '🤖', rgb: '59,130,246' },
    { key: 'aiCompletedTests', label: 'AI Tests', emoji: '✨', rgb: '6,182,212' },
    { key: 'mergeRequests', label: 'MRs', emoji: '🦁', rgb: '245,158,11' },
    { key: 'totalCommits', label: 'Commits', emoji: '⚙️', rgb: '100,116,139' },
]

const TONE_STYLE = {
    good: { background: 'rgba(16,185,129,0.15)', color: '#34D399', border: '1px solid rgba(16,185,129,0.35)' },
    info: { background: 'rgba(59,130,246,0.15)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.35)' },
    warn: { background: 'rgba(245,158,11,0.15)', color: '#FBBF24', border: '1px solid rgba(245,158,11,0.35)' },
}

export default function QaTeamComparison({ members, headerAction }) {
    const roster = useMemo(() => members.filter((m) => m.qaTeam && m.score > 0), [members])
    const [sortKey, setSortKey] = useState('score')

    const maxByCol = useMemo(() => {
        const map = {}
        for (const col of COLUMNS) map[col.key] = Math.max(1, ...roster.map((m) => Number(m[col.key]) || 0))
        return map
    }, [roster])

    const sorted = useMemo(
        () => [...roster].sort((a, b) => (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0)),
        [roster, sortKey]
    )

    const avg = useMemo(() => {
        const map = {}
        for (const col of COLUMNS) {
            const total = roster.reduce((s, m) => s + (Number(m[col.key]) || 0), 0)
            map[col.key] = roster.length ? Math.round(total / roster.length) : 0
        }
        return map
    }, [roster])

    const signals = useMemo(() => {
        if (roster.length === 0) return []
        const leaderOf = (key) => [...roster].sort((a, b) => (Number(b[key]) || 0) - (Number(a[key]) || 0))[0]
        const contributors = (key) => roster.filter((m) => (Number(m[key]) || 0) > 0).length

        const out = []
        const spotlight = leaderOf('score')
        if (spotlight) {
            out.push({
                tone: 'good',
                emoji: '🌟',
                text: `Spotlight: ${spotlight.name.split(' ')[0]} leads on overall impact (${spotlight.score} pts)`,
            })
        }
        const detector = leaderOf('bugsCreated')
        if (detector && detector.bugsCreated > 0) {
            out.push({
                tone: 'info',
                emoji: '🐛',
                text: `Detection anchor: ${detector.name.split(' ')[0]} (${detector.bugsCreated} bugs found)`,
            })
        }
        const automator = leaderOf('storiesAutomated')
        const autoContributors = contributors('storiesAutomated')
        if (automator && automator.storiesAutomated > 0) {
            out.push({
                tone: autoContributors <= 2 ? 'warn' : 'info',
                emoji: '🤖',
                text:
                    autoContributors <= 2
                        ? `Automation risk: only ${autoContributors} engineer${autoContributors === 1 ? '' : 's'} automating — anchored by ${automator.name.split(' ')[0]}`
                        : `Automation anchor: ${automator.name.split(' ')[0]} (${automator.storiesAutomated} stories)`,
            })
        }
        const quietest = [...roster].sort((a, b) => a.score - b.score)[0]
        if (quietest && roster.length > 2 && quietest.score < avg.score * 0.4) {
            out.push({
                tone: 'warn',
                emoji: '👀',
                text: `Check-in: ${quietest.name.split(' ')[0]} is tracking below team average this period`,
            })
        }
        return out.slice(0, 4)
    }, [roster, avg.score])

    const stickyTd = {
        position: 'sticky',
        left: 0,
        zIndex: 10,
        background: PULSE_COLORS.cardBg,
    }

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        📊 Team Comparison
                    </h3>
                    <p style={{ marginTop: '2px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                        Compare every engineer across the quality pillars — click a column to sort. Shading = relative
                        strength.
                    </p>
                </div>
                {headerAction}
            </div>

            {signals.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {signals.map((s, i) => (
                        <span
                            key={i}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                ...TONE_STYLE[s.tone],
                            }}
                        >
                            <span>{s.emoji}</span>
                            {s.text}
                        </span>
                    ))}
                </div>
            )}

            {sorted.length === 0 ? (
                <p style={{ fontSize: '13px', color: PULSE_COLORS.textMuted }}>
                    No QA activity in the selected window.
                </p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        ...stickyTd,
                                        textAlign: 'left',
                                        fontWeight: 500,
                                        color: PULSE_COLORS.textSubtle,
                                        fontSize: '12px',
                                        padding: '8px 12px 8px 0',
                                    }}
                                >
                                    Engineer
                                </th>
                                {COLUMNS.map((col) => (
                                    <th key={col.key} style={{ padding: '8px 4px' }}>
                                        <button
                                            onClick={() => setSortKey(col.key)}
                                            title={`Sort by ${col.label}`}
                                            style={{
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '2px',
                                                fontSize: '11px',
                                                fontWeight: 500,
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color:
                                                    sortKey === col.key ? PULSE_COLORS.text : PULSE_COLORS.textSubtle,
                                            }}
                                        >
                                            <span>{col.emoji}</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                {col.label}
                                                {sortKey === col.key && <span style={{ fontSize: '9px' }}>▼</span>}
                                            </span>
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((m, idx) => {
                                const ac = avatarColor(m.name)
                                return (
                                    <tr key={m.name} style={{ borderTop: `1px solid ${PULSE_COLORS.border}` }}>
                                        <td style={{ ...stickyTd, padding: '8px 12px 8px 0' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span
                                                    style={{
                                                        width: '16px',
                                                        textAlign: 'center',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        color: PULSE_COLORS.textSubtle,
                                                    }}
                                                >
                                                    {idx + 1}
                                                </span>
                                                <span
                                                    style={{
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#fff',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        flexShrink: 0,
                                                        backgroundColor: ac,
                                                    }}
                                                >
                                                    {getInitials(m.name)}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: PULSE_COLORS.text,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {m.name.split(' ')[0]}
                                                </span>
                                            </div>
                                        </td>
                                        {COLUMNS.map((col) => {
                                            const value = Number(m[col.key]) || 0
                                            const intensity = value / maxByCol[col.key]
                                            const isLeader = value > 0 && value === maxByCol[col.key]
                                            return (
                                                <td key={col.key} style={{ padding: '4px', textAlign: 'center' }}>
                                                    <div
                                                        style={{
                                                            margin: '0 auto',
                                                            minWidth: '34px',
                                                            borderRadius: '6px',
                                                            padding: '4px 0',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            fontVariantNumeric: 'tabular-nums',
                                                            backgroundColor:
                                                                value > 0
                                                                    ? `rgba(${col.rgb},${0.12 + intensity * 0.5})`
                                                                    : 'transparent',
                                                            color:
                                                                value === 0
                                                                    ? PULSE_COLORS.textSubtle
                                                                    : intensity > 0.55
                                                                      ? '#fff'
                                                                      : `rgb(${col.rgb})`,
                                                            outline: isLeader ? `1.5px solid rgb(${col.rgb})` : 'none',
                                                        }}
                                                    >
                                                        {value}
                                                    </div>
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                            <tr style={{ borderTop: `2px solid ${PULSE_COLORS.textSubtle}` }}>
                                <td
                                    style={{
                                        ...stickyTd,
                                        padding: '8px 12px 8px 0',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: PULSE_COLORS.textSubtle,
                                    }}
                                >
                                    Team avg
                                </td>
                                {COLUMNS.map((col) => (
                                    <td
                                        key={col.key}
                                        style={{
                                            padding: '8px 4px',
                                            textAlign: 'center',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: PULSE_COLORS.textSubtle,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {avg[col.key]}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
